import React, {useEffect, useState} from 'react'
import {useSolv} from "./components/SolvProvider";
import {useIntlEx} from "./components/IntlUtils.js"
import {closeDialog, ModalDialog} from "./components/DialogUtils";
import {ActionButton, Alert, FormGroup, useAlert} from "./components/FormComps";
import {mkExtraParams} from "./components/TableComps";
import {shortIdEqual} from "./components/StringUtils";
import {mkHref} from "./components/AccountUtils";
import getLogger from "./components/Logging.js"
import {AsyncPaginate} from "react-select-async-paginate";
import './App.css'
import {useAccessLevelOpts} from "./components/UserUtils";
import {TagBox, TagLabel} from "./components/TagBox";

const log = getLogger("MemberDeleteDialog")

export default function MemberDeleteDialog(props)  {

  const {api, session, setBusy, setFatal} = useSolv()
  const {intl} = useIntlEx()

  const [alert, setAlert] = useAlert()

  const [tenant, setTenant] = useState(null)
  const [row, setRow] = useState(null)

  const {findAccessLevelOpt, findEqualOrHigherAccessLevelOpts} = useAccessLevelOpts()

  const [newResourceOwner, setNewResourceOwner] = useState(null)

  useEffect(() => {
    if (props.tenant) {
      setTenant(props.tenant)
    }
    if (props.row) {
      setRow(props.row)
    }
  }, [props])

  async function loadNewResourceOwner(search, loadedOptions, cursor) {

    const filters = {
      userLevel: findEqualOrHigherAccessLevelOpts(row.userLevel)?.map(a => a.value)
      // userLevel: findInvitableAccessLevelOpts(tenant, session?.user?.userLevel)?.map(a => a.value) || [5, 10, 50]
    }

    // // log.debug("loadNewResourceOwner:", search, filters, loadedOptions, cursor)

    return api.listUsers({tenantId: tenant.tenantId, extraParams: mkExtraParams({filterParams: filters, sortParams: {displayName: "ASC"}}), cursor: cursor})
      .then(({data, nextCursor}) => {
        let opt = data.map(c => {
          return {
            value: c.userId,
            label: <div>
              <div>
                {c.displayName}
              </div>
              <TagBox>
                <TagLabel label={findAccessLevelOpt(c.userLevel)?.text} size="sm" className="d-span"/>
                {
                  shortIdEqual(c.userId, session.user.userId) &&
                    <TagLabel label={intl.msg("you")} size="sm" className="d-span"/>
                }
              </TagBox>
            </div>,
            userLevel: c.userLevel,
          };
        });
        opt = opt.filter(o => !shortIdEqual(o.value, props.row.userId))
        let res = {
          options: opt,
        }
        if (nextCursor) {
          res = {
            ...res,
            hasMore: true,
            additional: {
              cursor: nextCursor
            }
          }
        }
        // log.debug("loadNewResourceOwner: res=", res)
        return res
      })
      .catch((error) => {
        // log.debug("Error loading reseller: ", error)
      })
  }

  async function handleNewResourceOwnerChange(v) {
    setNewResourceOwner(v)
  }

  function handleDeleteClick() {
    setBusy(intl.msg("deleting"))
    let payload = {}
    if (!isInvitation()) {
      payload = {
        newResourceOwner: newResourceOwner?.value
      }
    }
    // log.debug("Delete member: payload=", payload)
    api.deleteUser(props.tenant.tenantId, props.row.userId, payload)
        .then(({data}) => {
          // log.debug("Delete member: data=", data)
          window.location = mkHref({suffix: `/members`, tenant: props.tenant})
        })
        .catch((error) => {
          // log.debug("Error deleting invitation")
          setAlert({type: "error", text: intl.msg("error_failed")})
        })
        .finally(() => {
          setBusy(null)
        })
  }

  function isInvitation() {
    return ["INVITING", "ONBOARDING"].includes(props.row?.status)
  }

  function handleOpenDialog() {
    // log.debug("handleOpenDialog: tenant=, row=", props.tenant, props.row)
    if (!isInvitation() && props.row?.userLevel === 100) {
      setAlert({type: "error", text: intl.msg("member_delete_error_owner")})
    }
    else {
      setAlert(null)
    }
  }

  function handleCloseDialog() {
    closeDialog("dlg_member_delete")
  }

  return (
    props.row &&
      <>
        <ModalDialog
          id="dlg_member_delete"
          size="md"
          keyboard="true"
          backdrop="static"
          onOpen={handleOpenDialog}
          onClose={handleCloseDialog}>

          <ModalDialog.Header title={intl.msg(`member_delete${isInvitation() ? "_invitation" : ""}_title`)} description={intl.msg(`member_delete${isInvitation() ? "_invitation" : ""}_description`)} alert={alert}/>

          {
            !alert &&
              <ModalDialog.Body>

                <Alert alert={{type: "warning", text: intl.msg(`member_delete${isInvitation() ? "_invitation" : ""}_text`, {displayName: isInvitation() ? props.row?.emailAddress : props.row?.displayName})}}/>

                {
                  !isInvitation() &&
                    <FormGroup className="mt-3">
                      <FormGroup.Label htmlFor="inp_newResourceOwner" text={intl.msg("member_delete_new_resource_owner")} description={intl.msg("member_delete_new_resource_owner_description")}/>
                      <FormGroup.Control>
                        <AsyncPaginate
                          id="inp_newResourceOwner"
                          className="react-select"
                          classNamePrefix="react-select"
                          cacheOptions
                          value={newResourceOwner}
                          debounceTimeout={800}
                          defaultOptions={true}
                          hideSelectedOptions={true}
                          placeholder={null}
                          loadOptions={loadNewResourceOwner}
                          onChange={handleNewResourceOwnerChange}
                        />
                      </FormGroup.Control>
                    </FormGroup>
                }

              </ModalDialog.Body>
          }

          <ModalDialog.Footer>
            <ModalDialog.Footer.Controls>
              <ActionButton name="cancel" flavor="secondary" onClick={handleCloseDialog}/>
              <ActionButton name="delete" confirm={intl.msg("confirm_delete")} flavor="primary" disabled={alert || (!["INVITING", "ONBOARDING"].includes(props.row.status) && !newResourceOwner)} onClick={handleDeleteClick}/>
            </ModalDialog.Footer.Controls>
          </ModalDialog.Footer>

        </ModalDialog>
      </>
  )
}